/**
 * 브랜드 명칭·법인 정보 SSoT.
 * 셸(헤더·푸터)·문서 타이틀 등 하드코딩 문구는 전부 이 설정을 참조한다.
 * 출처: design-prototypes (고객 합의 최종안). 명칭/정보 변경 시 여기만 수정한다.
 */
export const brand = {
  /**
   * 사이트 정식 origin — canonical·OG url·sitemap `<loc>`·JSON-LD 절대 URL 의 단일 기준.
   *
   * 검색엔진은 http/https·www 유무를 별개 URL 로 취급하므로, 여기 값 하나로 정식 주소를 고정한다
   * (vite.config.ts 의 sitemap 생성도 이 상수를 import 해 프리렌더 canonical 과 1:1 로 맞춘다).
   * 도메인 이전 시 여기만 수정한다 — 절대 URL 을 페이지·데이터에 재하드코딩하지 않는다.
   */
  origin: 'https://daipro1st.co.kr',
  /** 서비스 워드마크 */
  name: '대입로1st',
  /** 문서 타이틀·메타 설명용 한 줄 */
  tagline: 'AI 입시분석',
  /** 로고 슬로건(태그라인 포함 로고의 문구) — alt·마케팅 문구 재사용 지점 */
  slogan: 'AI가 만드는, 나만의 대학 합격 로드맵',
  /**
   * 로고 이미지 SSoT — public/assets 경로.
   *
   * 2축으로 고른다:
   *  - **wordmark / full**: 표시 크기. 헤더처럼 높이 20~30px 면 태그라인이 뭉개져 읽히지 않으므로
   *    워드마크 단독(`wordmark`)을 쓰고, 로그인·랜딩처럼 60px 이상 크게 놓는 자리만 `full`.
   *  - **기본 / `*Light`**: 배경 명도. 원본은 네이비 기조라 다크 배경(`--footer-bg`)에서 60% 픽셀이
   *    대비 3:1 미만으로 묻힌다. 다크 면에는 반드시 `*Light` 를 쓴다.
   *
   * 🔴 CSS 필터(`brightness-0 invert`)로 대체하지 말 것 — 로고를 단색 흰색으로 뭉개 그린 그라디언트를
   *    잃는다. `*Light` 는 흰 글자 + 브랜드 그린을 유지한 별도 자산이다.
   */
  logos: {
    /** 워드마크 단독(컬러) — 밝은 배경의 헤더·셸용 */
    wordmark: { src: '/assets/logo-wordmark.png', alt: '대입로1st' },
    /** 워드마크 단독(라이트) — 다크 배경용. 디자인 공급 화이트 원본(logo-white) 기준. */
    wordmarkLight: { src: '/assets/logo-wordmark-light.png', alt: '대입로1st' },
    /** 태그라인 포함(컬러) — 밝은 배경에 크게 노출하는 자리 */
    full: { src: '/assets/logo-full.png', alt: '대입로1st — AI가 만드는, 나만의 대학 합격 로드맵' },
    /** 태그라인 포함(라이트) — 다크 배경에 크게 노출하는 자리 */
    fullLight: {
      src: '/assets/logo-full-light.png',
      alt: '대입로1st — AI가 만드는, 나만의 대학 합격 로드맵',
    },
  },
  /**
   * 사업자 정보(푸터·사업자정보 페이지 공용 SSoT).
   *
   * `tel` 은 대표전화 미개통 상태라 빈 문자열이다 — 값이 비면 푸터·`/business` 행이 렌더링되지
   * 않는다(항목만 있고 값이 빈 고지는 오히려 오인 소지). 개통 시 여기만 채우면 양쪽에 함께 노출된다.
   * ⚠️ 대표전화는 전자상거래법 통신판매업 의무 고지 항목이므로 개통 후 반드시 채울 것.
   */
  legal: {
    company: '(주)제이앤디소프트',
    ceo: '한준호',
    bizNo: '757-88-02437',
    mailOrderNo: '제2026-서울강서-2398호',
    address: '서울특별시 강서구 양천로 424, 데시앙플렉스지식산업센터 630호',
    tel: '',
    email: 'jnddev@jndsoft.co.kr',
    copyright: '© 2026 JnDSOFT Inc. All rights reserved.',
  },
  /** 외부 상담 문의 링크 */
  consultUrl: 'https://deolux.or.kr/문의하기/',
  /**
   * 카카오톡 채널 1:1 채팅 URL — 랜딩 플로팅 문의 버튼의 목적지.
   * 채널 개설 후 `http://pf.kakao.com/_채널ID/chat` 로 채운다.
   * 빈 문자열이면 플로팅 버튼이 렌더링되지 않는다(채널 미개설 상태 안전장치).
   */
  kakaoChannelChatUrl: 'https://pf.kakao.com/_xeZTBX/chat',
  /**
   * 네이버 블로그 URL — 푸터 브랜드 열의 블로그 아이콘 링크 목적지.
   * 빈 문자열이면 아이콘이 렌더링되지 않는다(운영 중단 시 안전장치).
   * 블로그가 입시 컨설팅 유입 채널이라 PG(토스) 연동 심사에 우회결제 유도로
   * 비칠 수 있어 심사 통과 전까지 비활성 — 통과 후 아래 주석 URL 로 교체한다.
   */
  naverBlogUrl: '', // 'https://blog.naver.com/deolux311'
  /**
   * 워크스페이스 진입 경로 SSoT — 로그아웃 복귀·로그인 리다이렉트 등 "워크스페이스 홈"을
   * 가리키는 여러 지점이 이 값 하나를 공유한다.
   */
  crossLinks: {
    /** AI 입시분석 워크스페이스 홈 — 경로 세그먼트 제거 후 사이트 루트가 곧 워크스페이스 랜딩이다. */
    analysis: '/',
  },
} as const;

export type Brand = typeof brand;

/**
 * 사이트 내부 경로를 **정식(canonical) 절대 URL** 로 만든다 — canonical·OG url·sitemap `<loc>`·
 * JSON-LD `item`/`url` 등 검색엔진에 노출되는 모든 절대 URL 의 단일 생성 지점.
 *
 * ## 왜 trailing slash 인가
 *
 * 프리렌더는 `dirStyle: 'nested'`(vite.config.ts)라 `/guide/yonsei` 가
 * `dist/guide/yonsei/index.html` 로 생성되고, Nginx `try_files $uri $uri/` 가 이를 **디렉토리**로
 * 매칭한다. nginx 는 디렉토리 서빙 시 **반드시 슬래시 URL 로 301 한 뒤** index 를 낸다 — 이는
 * nginx 내장 동작이라 끌 수 없다(`absolute_redirect off` 는 Location 을 상대경로로 바꿀 뿐
 * 301 자체를 없애지 못한다 — 과거 이 지시어를 오해해 슬래시 없는 canonical 을 발행했다).
 *
 * 그 결과 슬래시 없는 URL 을 canonical 로 선언하면 **자기 자신이 리다이렉트 대상**이 되어,
 * 구글이 사용자 선언 canonical 을 버리고 자체 판단(슬래시 붙은 형태)을 쓴다
 * (2026-08 GSC "리디렉션이 포함된 페이지" 12건 = sitemap 전 URL). 따라서 실제 200 을 내는
 * 형태(슬래시 있음)를 정규형으로 확정하고, 절대 URL 은 전부 이 함수를 경유시킨다.
 *
 * @param path 선행 슬래시로 시작하는 내부 경로(예: `/guide/yonsei`). 쿼리·해시는 넣지 않는다.
 * @returns 정규 절대 URL(항상 `/` 로 끝남) — 루트는 `https://origin/`.
 */
export function canonicalUrl(path: string): string {
  return `${brand.origin}${canonicalPath(path)}`;
}

/**
 * 내부 경로를 정규형(항상 trailing slash)으로 맞춘다 — 절대 URL 이 아닌 `<a href>`·sitemap 경로용.
 * 근거는 {@link canonicalUrl} 참조.
 */
export function canonicalPath(path: string): string {
  return path.endsWith('/') ? path : `${path}/`;
}
